const REQUIRED_AUTHORITIES = Object.freeze([
  'BR_PHASE15_AUTHORITATIVE_RUNTIME_EVENTS_SHADOW',
  'BR_PHASE3_DURABLE_LIFECYCLE_SHADOW',
  'BR_PHASE3_LISTEN_LIFECYCLE_AUTHORITY',
  'BR_CQ1_RESPONSE_PLAN_AUTHORITY',
  'BR_CQ2_CONVERSATIONAL_EVIDENCE_AUTHORITY',
  'BR_CQ3_NATURAL_REALIZATION_AUTHORITY',
  'BR_CQ3_VOICE_HOLD_FLOOR_AUTHORITY',
]);

const bounded = (value) => String(value || '').trim().slice(0, 160);

/**
 * Interpret the content-free readiness response for Live Voice.
 *
 * This creates no authority.  A bound result only records that the server
 * evaluated the exact thread ChatShell is about to use.  `/process` performs
 * the complete authoritative evaluation again for every accepted turn.
 */
export function classifyVoiceCanaryReadiness({ responseOk, statusCode, payload, threadId }) {
  const body = payload && typeof payload === 'object' ? payload : {};
  const requestedThread = bounded(threadId);
  if (body.status === 'READY') {
    const authorities = body.effective_authorities || {};
    const completeAuthorities = REQUIRED_AUTHORITIES.every(
      (name) => authorities[name] === true,
    );
    const valid = Boolean(
      responseOk &&
      requestedThread &&
      body.owner_in_scope === true &&
      body.thread_owned === true &&
      body.live_voice === true &&
      body.build_matches === true &&
      bounded(body.build) &&
      bounded(body.configuration_digest) &&
      bounded(body.activation_epoch) &&
      completeAuthorities
    );
    if (!valid) return { kind: 'blocked', reason: 'readiness_contract_invalid' };
    return {
      kind: 'bound',
      binding: {
        threadId: requestedThread,
        build: bounded(body.build),
        configurationDigest: bounded(body.configuration_digest),
        activationEpoch: bounded(body.activation_epoch),
      },
    };
  }

  // A person outside the scoped experiment retains the ordinary Live Voice
  // path.  A scoped owner whose requested thread/configuration is not ready
  // must fail closed before microphone acquisition.
  if (body.canary_applicable === false && statusCode === 403) {
    return { kind: 'legacy', binding: null };
  }
  return {
    kind: 'blocked',
    reason: bounded(body.binding_status || body.reason) || 'readiness_unavailable',
  };
}

export function voiceCanaryBindingMatches(binding, threadId) {
  return Boolean(binding && bounded(threadId) && binding.threadId === bounded(threadId));
}

export function voiceCanaryReadinessMatchesBinding(binding, nextBinding) {
  return Boolean(
    binding &&
    nextBinding &&
    binding.threadId === nextBinding.threadId &&
    binding.build === nextBinding.build &&
    binding.configurationDigest === nextBinding.configurationDigest &&
    binding.activationEpoch === nextBinding.activationEpoch
  );
}

export { REQUIRED_AUTHORITIES };
